import { Injectable } from '@angular/core';
import { AlertDisplayType } from "@commonEnums/AlertDisplayType";
import { AlertService } from '@commonServices/alert.service';
import { RequestService } from '@commonServices/request.service';
import { AlertPositionType } from '@enums/alertPositionType';
import { Client as AccountAPI, FileResponse, GlobalRoleAssignmentInfosDto, OnBoardingDto, RoleAssignmentDto, RoleAssignmentInfoDto } from '@interfaces/HttpClient/AccountApiModels';
import { AccountLanguage } from '@interfaces/HttpClient/AccountApiPublicModels';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { OnboardingService } from './onboarding.service';
import { TranslationService } from './translation.service';

@Injectable()
export class AdministratorService {

	roleAssignmentInfosCache: GlobalRoleAssignmentInfosDto

	constructor(
		private accountApi: AccountAPI,
		private requestService: RequestService,
		private onboardingService: OnboardingService,
		private translationService: TranslationService,
		private alertService: AlertService<AlertPositionType>
	) { }

	// ===== CREATE =====
	async createOnBoarding(email: string | null, language: AccountLanguage, roleAssignment: RoleAssignmentDto): Promise<OnBoardingDto> {
		return this.onboardingService.onBoardUserByEmail(email, language, roleAssignment)
	}

	// ===== READ =====
	async getRoleAssignmentInfos(): Promise<GlobalRoleAssignmentInfosDto> {
		if (this.roleAssignmentInfosCache) {
			return this.roleAssignmentInfosCache
		} else {
			try {
				this.roleAssignmentInfosCache = await firstValueFrom(this.accountApi.administrator_GetRoleAssignmentInfos())
				return this.roleAssignmentInfosCache;
			} catch (e) {
				return this.requestService.errorHandling(e, null, [
					{ status: 204, return: undefined }
				]);
			}
		}
	}
	async getCsvDownload(): Promise<FileResponse> {
		try {
			return firstValueFrom(this.accountApi.administrator_GetCsvExportForLicenseAndCatalogInfo());
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getEmployeeCsvDownload(): Promise<FileResponse> {

		try {
			return firstValueFrom(this.accountApi.administrator_GetCsvExportForEmployeesInfo());
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	// ==== UPDATE =====
	async updateRoleAssignment(updatedRoleAssignment: RoleAssignmentInfoDto): Promise<RoleAssignmentInfoDto> {
		try {
			return await firstValueFrom(this.accountApi.administrator_UpdateRoleAssignment(updatedRoleAssignment))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	// ===== DELETE =====
	async deleteOnboarding(onboardingId: number): Promise<void> {
		try {
			await firstValueFrom(this.accountApi.administrator_DeleteOnBoarding(onboardingId))
			const successMessage = this.translationService.getByKey("global.alert.delete-success", { beginning: this.translationService.getByKey("global.roles.label") })
			this.alertService.sendMessage(AlertPositionType.Global, AlertDisplayType.Success, successMessage)
			return
		} catch (e) {
			this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
			const errorMessage = this.translationService.getByKey("global.alert.delete-server-failure", { beginning: this.translationService.getByKey("global.roles.label") })
			this.alertService.sendMessage(AlertPositionType.Global, AlertDisplayType.Danger, errorMessage)
			return
		}
	}
	async deleteRoleAssignment(roleAssignmentId: number): Promise<boolean> {
		try {
			await firstValueFrom(this.accountApi.administrator_DeleteRoleAssignment(roleAssignmentId))
			return true
		} catch (e) {
			this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
			return false
		}
	}
}